/**
 * CoffeeMates Push Notification Service
 * Handles browser push subscription via Web Push API + VAPID
 */

import httpClient from '../utils/httpClient';

const SW_PATH = '/sw.js';

class PushService {
  private registration: ServiceWorkerRegistration | null = null;

  /** Check if push is supported in this browser */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /** Get current permission state */
  getPermissionState(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  /** Register service worker and subscribe to push */
  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return false;
      }

      // Get VAPID public key from server
      const { data } = await httpClient.get('/users/me/push/vapid-key');
      const vapidKey = data.vapid_public_key;

      if (!vapidKey) {
        return false;
      }

      // Subscribe — cast Uint8Array to BufferSource. TS 5.7 narrows
      // Uint8Array.buffer to ArrayBufferLike (which includes
      // SharedArrayBuffer); applicationServerKey only accepts ArrayBuffer.
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      // Send subscription to backend
      const subJson = subscription.toJSON();
      await httpClient.post('/users/me/push/subscribe', {
        endpoint: subJson.endpoint,
        p256dh: subJson.keys?.p256dh || '',
        auth: subJson.keys?.auth || '',
      });

      return true;
    } catch (err) {
      // Subscription failed — caller toggles UI back to "not subscribed".
      // Common causes: SW registration blocked, VAPID key fetch failed,
      // pushManager.subscribe() rejected. Log for observability; httpClient
      // already logs HTTP-layer failures.
      console.warn('[pushService] subscribe failed', err);
      return false;
    }
  }

  /** Unsubscribe from push */
  async unsubscribe(): Promise<boolean> {
    try {
      if (this.registration) {
        const subscription = await this.registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      await httpClient.del('/users/me/push/unsubscribe');
      return true;
    } catch (err) {
      console.warn('[pushService] unsubscribe failed', err);
      return false;
    }
  }

  /** Check if currently subscribed */
  async isSubscribed(): Promise<boolean> {
    if (!this.isSupported()) return false;
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      if (!reg) return false;
      const sub = await reg.pushManager.getSubscription();
      return !!sub;
    } catch (err) {
      console.warn('[pushService] isSubscribed check failed', err);
      return false;
    }
  }

  /** Convert VAPID key from base64 to Uint8Array */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }
}

const pushService = new PushService();
export default pushService;
